import { createSignal, createMemo, For, Show, createEffect, on } from 'solid-js';
import { Button, Input } from '@bz/ui';
import { useCurrency } from '@bz/common';
import { getCoinIcon } from '@bz/wallet/src/utils';

import { useBetSlipStore } from '../../useBetSlip';

const CurrencySuffix = (props: { currency: string }) => (
  <span class="center text-neutral-main1 text-sm font-semibold w-5 h-5">
    <img class="size-5" src={getCoinIcon(props.currency)} alt={props.currency} />
  </span>
);

export const QuickBetPanel = () => {
  const [store, dispatch] = useBetSlipStore();
  const { latestCurrency } = useCurrency();

  // 如果 store.quickBetAmount 没有值，则使用 fastBetOptions 的第一个值作为默认值
  const defaultAmount = createMemo(() => {
    if (store.quickBetAmount) return store.quickBetAmount;
    if (store.fastBetOptions && store.fastBetOptions.length > 0) return store.fastBetOptions[0];
    return undefined;
  });

  const [isApplied, setIsApplied] = createSignal(!!defaultAmount());
  const [amount, setAmount] = createSignal<number | string | undefined>(defaultAmount());

  // 初始化时同步到 store，确保 OddsButton 能获取到值
  createEffect(() => {
    if (!store.quickBetAmount && defaultAmount()) {
      dispatch({ type: 'SET_QUICK_BET_AMOUNT', quickBetAmount: Number(defaultAmount()) });
    }
  });

  const [, setIsFocus] = createSignal(false);

  const currentCurrencyInfo = createMemo(() => currencyConfig[latestCurrency()]);

  //快速金额发生变化时，更新默认金额
  createEffect(
    on(
      () => store.fastBetOptions,
      (options) => {
        if (options && options.length > 0) {
          const defaultVal = options[0];
          setAmount(defaultVal);
          setIsApplied(true);
          dispatch({
            type: 'SET_QUICK_BET_AMOUNT',
            quickBetAmount: Number(defaultVal),
          });
        }
      },
      { defer: true }
    )
  );

  const updateAmount = (val: number | string | undefined) => {
    setAmount(val);
    if (isApplied()) setIsApplied(false);
  };

  const minBet = createMemo(() => currentCurrencyInfo()?.minBet ?? 0);
  const isBelowMin = createMemo(() => {
    const val = Number(amount());
    return val > 0 && val < minBet();
  });

  const handleApply = () => {
    const val = Number(amount());
    dispatch({ type: 'SET_QUICK_BET_AMOUNT', quickBetAmount: val });
    setIsApplied(true);
  };

  const applyDisabled = createMemo(() => {
    const val = amount();
    return isBelowMin() || val === undefined || val === null || val === 0;
  });

  return (
    <div class="flex border-t border-main3 border-solid">
      <div class="px-4 py-4 flex flex-col gap-2 bg-betslip-header-bg">
        <div class="text-[14px] leading-[150%] text-neutral-main2">
          <span>
            {t(
              'QuickBet mode is on! After single click on any selection, it will place your bet immediately. See all your bets on'
            )}
          </span>
          <span class="inline-flex items-center align-bottom ml-1">
            <IconMyBets class={cx('size-4 text-neutral-main2 mr-1')} />
            <a href="/my-bets" class="text-neutral-main2 underline">
              {t('My Bets page')}
            </a>
          </span>
        </div>

        <div class="flex gap-2 h-10 items-center">
          <div class="relative w-full flex-1">
            <Input.Number
              value={amount() as any}
              onInput={(val: string) => updateAmount(val)}
              onFocus={() => setIsFocus(true)}
              onBlur={() => setIsFocus(false)}
              suffix={
                <div class="flex items-center">
                  <CurrencySuffix currency={latestCurrency()} />
                  <Show when={!isApplied()} fallback={<IconCheckmark class="size-6 text-brand-default ml-2" />}>
                    <Button
                      disabled={applyDisabled()}
                      class="w-[82px] ml-2 caption-s-semibold px-3 bg-button-general-primary text-neutral-alw-black rounded-[4px] h-6 whitespace-nowrap"
                      classList={{ 'opacity-40': applyDisabled() }}
                      onClick={handleApply}
                    >
                      {t('APPLY BET')}
                    </Button>
                  </Show>
                </div>
              }
              class="w-full bg-layer3 border-main3 h-10 font-semibold focus:border-primary-main transition-opacity rounded-lg text-neutral-main1"
              placeholder={t('Stake Amount')}
            />
          </div>
        </div>

        <Show when={isBelowMin()}>
          <div
            class="text-xs cursor-pointer flex bg-alpha-function-orange-a10 py-2 rounded-[0.25rem] mt-0 items-center"
            onClick={() => updateAmount(minBet())}
          >
            <IconCheckWarning class="size-6 text-function-alert mx-2" />
            <div class="flex flex-col">
              <span class="text-neutral-main1 font-semibold">
                {t('Minimum stake must be above')} {minBet()}
              </span>
              <span class="text-function-alert_alt font-semibold">{t('Tap to update stake amount')}</span>
            </div>
          </div>
        </Show>

        <div class="flex gap-2">
          <For each={store.fastBetOptions}>
            {(optAmount) => {
              const isSelected = () => Number(amount()) === optAmount;
              return (
                <Button
                  type="second"
                  class={cx(
                    'flex-1 h-9 rounded-lg border border-main3 bg-button-general-secondary caption-m-semibold text-neutral-main1',
                    isSelected() ? '!border-brand !text-brand-default' : ''
                  )}
                  onClick={() => updateAmount(optAmount)}
                >
                  {optAmount}
                </Button>
              );
            }}
          </For>
        </div>

        <div class="text-xs text-neutral-main2 mt-2 flex items-center gap-1">
          {t('Quick Bet is active with')}
          <img class="size-3" src={getCoinIcon(latestCurrency())} alt={latestCurrency()} />
          <span class="text-neutral-main1 font-semibold">{Number(store.quickBetAmount ?? 0).toFixed(2)}</span>
          {t('bet')}
        </div>
      </div>
    </div>
  );
};
